import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {AmberDot} from '@/components/amber-dot'
import {ChevronGlyph} from '@/components/ui/icons'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {useAuth} from '@/hooks/platform/use-auth'
import {useResendVerification} from '@/hooks/empresa/identidad/use-email-verification'

function usePendienteDeConfirmar(): boolean {
  const {user} = useAuth()
  return !!user && !!user.email && !user.email_verified
}

/** El `catch` marca «enviado» a propósito: no se le confirma a nadie si el correo existe en el sistema. */
function useReenvio() {
  const resend = useResendVerification()
  const [enviado, setEnviado] = useState(false)
  return {
    enviado,
    enviando: resend.isPending,
    reenviar: () =>
      resend
        .mutateAsync()
        .then(() => setEnviado(true))
        .catch(() => setEnviado(true)),
  }
}

function BotonReenviar({
  testId,
  className,
  enviando,
  onReenviar,
}: {
  testId: string
  className: string
  enviando: boolean
  onReenviar: () => void
}) {
  const {t} = useTranslation()
  return (
    <button type="button" data-testid={testId} disabled={enviando} onClick={onReenviar} className={className}>
      {enviando ? t('emailVerify.bannerSending') : t('emailVerify.bannerAction')}
    </button>
  )
}

export function EmailVerifyChip() {
  const {t} = useTranslation()
  const {enviado, enviando, reenviar} = useReenvio()
  const pendiente = usePendienteDeConfirmar()

  if (!pendiente) return null

  // `Popover` trae Escape, el retorno del foco y el toque que exige T11.
  return (
    <Popover>
      <PopoverTrigger
        type="button"
        data-testid="email-verify-chip"
        aria-label={t('emailVerify.chipLabel')}
        className="topbar-link flex min-h-11 cursor-pointer items-center gap-1.5 px-2 py-1 outline-none focus-visible:ring-2 focus-visible:ring-ring/50 md:min-h-0">
        <AmberDot label={t('common.senal.pendiente')} />
        <span className="hidden font-mono text-[11px] font-semibold tracking-wide uppercase lg:inline">
          {t('emailVerify.chipShort')}
        </span>
        <ChevronGlyph className="opacity-70" />
      </PopoverTrigger>
      <PopoverContent align="end" aria-label={t('emailVerify.chipLabel')} className="w-64 p-3">
        <p className="text-sm">{enviado ? t('emailVerify.bannerSent') : t('emailVerify.bannerText')}</p>
        {!enviado && (
          <BotonReenviar
            testId="email-verify-chip-resend"
            enviando={enviando}
            onReenviar={reenviar}
            className="mt-2.5 w-full cursor-pointer rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-secondary"
          />
        )}
      </PopoverContent>
    </Popover>
  )
}

/** Solo en el Resumen; desaparece cuando `email_verified` es true o la cuenta no tiene correo. */
export function EmailVerifyBanner() {
  const {t} = useTranslation()
  const {enviado, enviando, reenviar} = useReenvio()
  const pendiente = usePendienteDeConfirmar()

  if (!pendiente) return null

  return (
    <div
      data-testid="email-verify-banner"
      className="notice-amber mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg px-3.5 py-2.5">
      <span className="flex min-w-0 items-center gap-2 text-sm">
        <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-current opacity-70" />
        <span className="min-w-0">{enviado ? t('emailVerify.bannerSent') : t('emailVerify.bannerText')}</span>
      </span>
      {!enviado && (
        <BotonReenviar
          testId="email-verify-resend"
          enviando={enviando}
          onReenviar={reenviar}
          className="ml-auto rounded-md border border-current/30 px-2.5 py-1 text-xs font-semibold hover:bg-current/10"
        />
      )}
    </div>
  )
}
